import { Box, Text, Button, Flex, Center, Spinner } from "@chakra-ui/react";
import { ClockIcon } from "@root/assets/icons/clockIcon";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useMemo } from "react";
import { EnrollButton, ImageCourse, RquirementLabel } from "../styles";
import { REQUIREMENT_TYPE } from "@root/constants";
import { formatDurationSmart, formatDateToISO } from "@root/utils/date-helper";
import useMediaQ from "@root/hooks/useMediaQ";
import defaultThumbnail from "@root/assets/images/Course Thunbnail.png";
import { CourseService } from "@root/services/course";

const Card: React.FC<CardProps> = ({ course, onClick }) => {
  const { t } = useTranslation("common");
  const [loaded, setLoaded] = useState(false);
  const { sm } = useMediaQ();
  const isMobile = !sm;
  const courseService = useMemo(() => new CourseService(), []);
  const [fetchedChapters, setFetchedChapters] = useState<any[] | null>(null);
  const [isFetchingChapters, setIsFetchingChapters] = useState(false);

  const image = course?.thumbnail;
  const maintitle = course?.title;
  const subtitle = course?.subtitle;
  const desc = course?.description;
  const requirement = course?.requirement;
  const status = course?.isCourseTaken?.status;
  const enrollmentStartDate = (course as any)?.enrollmentPeriodStart;
  const enrollmentEndDate = (course as any)?.enrollmentPeriodEnd;
  const courseId = course?.id;
  const enrollmentDuration = course?.enrollmentDuration;

  useEffect(() => {
    const courseAny = course as any;
    const hasChapters =
      (courseAny?.chapters?.rows && courseAny.chapters.rows.length > 0) ||
      (courseAny?.courseChapters?.rows &&
        courseAny.courseChapters.rows.length > 0) ||
      (Array.isArray(courseAny?.chapters) && courseAny.chapters.length > 0) ||
      (Array.isArray(courseAny?.courseChapters) &&
        courseAny.courseChapters.length > 0);

    if (!hasChapters && courseId && !fetchedChapters && !isFetchingChapters) {
      setIsFetchingChapters(true);
      courseService
        .fetchCourseChapters(Number(courseId))
        .then((response: any) => {
          const chapters =
            response?.data?.data?.rows || response?.data?.rows || [];
          if (chapters.length > 0) {
            setFetchedChapters(chapters);
          }
        })
        .finally(() => {
          setIsFetchingChapters(false);
        });
    }
  }, [courseId, course, courseService, fetchedChapters, isFetchingChapters]);

  const duration = useMemo((): string => {
    const courseAny = course as any;
    let chapters: any[] = [];

    if (courseAny?.chapters?.rows) chapters = courseAny.chapters.rows;
    else if (courseAny?.courseChapters?.rows)
      chapters = courseAny.courseChapters.rows;
    else if (Array.isArray(courseAny?.chapters)) chapters = courseAny.chapters;
    else if (Array.isArray(courseAny?.courseChapters))
      chapters = courseAny.courseChapters;
    else if (fetchedChapters) chapters = fetchedChapters;

    if (chapters.length > 0) {
      const total = chapters.reduce((total: number, chapter: any) => {
        const videos = chapter?.courseVideos || [];
        return (
          total +
          videos.reduce(
            (acc: number, video: any) => acc + (video?.duration || 0),
            0,
          )
        );
      }, 0);

      if (total > 0) return total.toString();
    }

    return course?.totalDuration || courseAny?.totalDuration || "0";
  }, [course, fetchedChapters]);

  const [imgSrc, setImgSrc] = useState<string | undefined>(
    image || defaultThumbnail,
  );

  useEffect(() => {
    setLoaded(false);
    setImgSrc(image || defaultThumbnail);
  }, [image]);

  const requirementLabel =
    requirement === REQUIREMENT_TYPE.REQUIRED
      ? t("coursesPage.essentialRequired")
      : requirement === REQUIREMENT_TYPE.OPTIONAL
        ? t("coursesPage.optionalRecommended")
        : null;

  const requirementColor =
    requirement === REQUIREMENT_TYPE.REQUIRED
      ? "#3A59D1"
      : requirement === REQUIREMENT_TYPE.OPTIONAL
        ? "#FE6807"
        : undefined;

  if (isMobile) {
    return (
      <Box
        w="100%"
        borderWidth="1px"
        borderRadius="lg"
        overflow="hidden"
        boxShadow="md"
        bg="white"
        p="12px"
        cursor={onClick ? "pointer" : undefined}
        onClick={onClick}
        display="flex"
        gap="12px"
      >
        <Box
          w="120px"
          h="120px"
          borderRadius="12px"
          overflow="hidden"
          position="relative"
        >
          <ImageCourse
            src={image || defaultThumbnail}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        </Box>

        <Flex direction="column" flex="1" justify="space-between">
          <Box>
            <Text fontWeight="600">{maintitle}</Text>
            <Text color="#1819B4">{subtitle}</Text>
            <Text noOfLines={2}>{desc}</Text>

            <Flex align="center" gap="1">
              <ClockIcon boxSize={4} />
              {formatDurationSmart(parseInt(duration || "0"))}
            </Flex>

            {/* {enrollmentStartDate && enrollmentEndDate && (
              <Text fontSize="12px">
                {formatDateToISO(new Date(enrollmentStartDate))} ~{" "}
                {formatDateToISO(new Date(enrollmentEndDate))}
              </Text>
            )}

            {enrollmentDuration && (
              <Text fontSize="12px">
                <strong>{t("courseDetail.enrollmentDuration")}: </strong>
                {enrollmentDuration} months
              </Text>
            )} */}
          </Box>

          {/* {status !== "enrolled" && status !== "completed" && (
            <Button
              mt={"4px"}
              height={"35px"}
              maxW={"fit-content"}
              onClick={onClick}
            >
              {t("userEnrollment.enroll")}
            </Button>
          )} */}
        </Flex>
      </Box>
    );
  }

  // ===== DESKTOP (FIXED HEIGHT — SAME SIZE AS REFERENCE CARD) =====
  return (
    <Box
      w="100%"
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      boxShadow="md"
      bg="white"
      p="8px"
      cursor={onClick ? "pointer" : undefined}
      onClick={onClick}
      display="flex"
      flexDirection="column"
      height="100%"
      maxH="400px"
    >
      <Box
        position="relative"
        h="220px"
        flexShrink={0}
        borderRadius="16px"
        overflow="hidden"
      >
        {!loaded && (
          <Center position="absolute" inset={0}>
            <Spinner />
          </Center>
        )}
        <ImageCourse
          src={imgSrc}
          onLoad={() => setLoaded(true)}
          onError={() => setImgSrc(defaultThumbnail)}
        />
      </Box>

      <Box px="12px" py="10px" display="flex" flexDirection="column" flex="1">
        {/* CONTENT */}
        <Flex direction="column" gap={2} flex="1">
          <Text fontWeight="600">{maintitle}</Text>
          <Text color="#1819B4">{subtitle}</Text>
          <Text noOfLines={2}>{desc}</Text>

          <Flex align="center" gap="1">
            <ClockIcon boxSize={6} />
            {formatDurationSmart(parseInt(duration || "0"))}
          </Flex>

          {/* {enrollmentStartDate && enrollmentEndDate && enrollmentDuration && (
            <Text fontSize="sm">
              {t("courseDetail.enrollmentDuration")}: {enrollmentDuration}{" "}
              months
            </Text>
          )}

          {enrollmentStartDate &&
            enrollmentEndDate &&
            enrollmentDuration == null && (
              <Text fontSize="sm">
                {t("courseDetail.enrollmentPeriod")}:{" "}
                {formatDateToISO(new Date(enrollmentStartDate))} ~{" "}
                {formatDateToISO(new Date(enrollmentEndDate))}
              </Text>
            )} */}
        </Flex>

        {/* ENROLL BUTTON — ALWAYS BOTTOM */}
        {/* {status !== "enrolled" && status !== "completed" && (
          <Flex justify="flex-end" mt="8px">
            <EnrollButton
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
              }}
            >
              {t("userEnrollment.enroll")}
            </EnrollButton>
          </Flex>
        )} */}
      </Box>
    </Box>
  );
};

export default Card;
